/* eslint-disable max-lines */
import React, { useCallback, useState } from 'react';
import { withTheme } from 'styled-components';
import _ from 'lodash';
import { styled } from '@this/constants/themes';

import type Trip from '@this/domain/trip/trip2';
import type OrderItem from '@this/domain/order_item2';
import OrderItemOriginalPrice from '@this/domain/order_item_original_price2';
import OrderItemPriceDetail from '@this/domain/order_item_price_detail2';
import type BulkTicket from '@this/domain/bulk_ticket2';
import type ShareholderTicketList from '@this/domain/shareholder_ticket_list/index';
import PriceField from '@this/shared/price_field/price_field';
import Text from '@this/shared/text/text';
import { AutocompleteSelect } from '@this/shared/ui/inputs/autocomplete';
import Button from '@this/shared/atoms/button';
import { Button as NewButton } from '@this/shared/ui/inputs/button/button';
import { Link } from '@this/shared/ui/navigations/link';
import type TaxType from '@this/domain/tax_type';
import { TAX_TYPE_ID } from '@this/domain/tax_type';
import UserSearchRepositoty from '@this/domain/arrangement/user_search.repository';
import type TransportElement from '@this/domain/transport_element2';
import type PaymentMethodList from '@this/src/domain/payment_method/payment_method_list';
import { Datepicker } from '@this/shared/ui/inputs/datepicker';
import moment from 'moment/moment';
import type { ShareholderInfo } from '@this/domain/other_service_shareholder_info';
import type { IndividualTargetSuppliedItem } from '@this/domain/individual_target_supplied_item';
import { reportError } from '@this/lib/bugsnag';
import TravelerInformation from '@this/domain/traveler_information';
import type { Element } from '@this/domain/order_item2';
import type { TransportType } from '@this/domain/transport_element2';
import { observer } from 'mobx-react';
import { useVirtualCounterContext } from '@this/src/components/arrangement/virtual_counter/virtual_counter_context';
import type User from '@this/src/domain/user/user2';
import type { Moment } from '@this/src/lib/moment';
import OrderItemSupplierItemSelect from './order_item_supplier_item_select';
import OrderItemTravelerForm from './order_item_traveler_form';

const TRAVELER_INFORMATION_TEMP_ID_RANGE = 1000000000;

interface Props {
  index: number;
  serviceId: number;
  selectedTrip: Trip;
  orderItem: OrderItem;
  bulkTickets: BulkTicket[];
  shareholderTickets: ShareholderTicketList;
  taxTypes: TaxType[];
  paymentMethods: PaymentMethodList;
  shareholderInfos: ShareholderInfo[];
  individualTargetSuppliedItems: IndividualTargetSuppliedItem[];
  theme: { themeClass: string };
  classNameForModifiedField: (...path: (string | number)[]) => string;
  changed?: () => void;
}

const OrderItemPriceForm: React.FC<Props> = ({
  index,
  serviceId,
  selectedTrip,
  orderItem,
  bulkTickets,
  shareholderTickets,
  taxTypes,
  paymentMethods,
  shareholderInfos,
  individualTargetSuppliedItems,
  theme,
  classNameForModifiedField,
  changed
}: Props) => {
  const [candidates, setCandidates] = useState<User[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [shownTravelerPopupId, setShownTravelerPopupId] = useState<number | null>(null);
  const [selectedBulkTickets, setSelectedBulkTickets] = useState<BulkTicket[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [openPriceDetails, setOpenPriceDetails] = useState(orderItem.orderItemCategory !== 'foreign_air');

  const { suppliedItems } = useVirtualCounterContext();

  const fetchCandidates = useCallback(
    async (query: string) => {
      setCandidatesLoading(true);
      const travelerUserIds = orderItem.travelerInformations.map(t => t.userId);
      try {
        const results = await UserSearchRepositoty.search(selectedTrip.user.organization?.id, query);
        const candidatesResult = results.filter(u => !travelerUserIds.includes(u.id));
        setCandidates(candidatesResult);
      } catch (error) {
        setErrors(prev => [...prev, '利用者候補の取得に失敗しました。']);
      } finally {
        setCandidatesLoading(false);
      }
    },
    [orderItem.travelerInformations, selectedTrip.user.organization?.id]
  );

  const handleBulkTicketChange = (index: number, value: number): void => {
    const price = orderItem.price;
    const prevTicketId = price?.bulkTicketIds?.[index];
    if (prevTicketId) {
      // すでに別のが紐付いていた
      const prevTicket = _.find(bulkTickets, t => t.id === Number(prevTicketId));
      if (prevTicket) {
        prevTicket.cancelUse();
      }
    }
    const nextTicket = _.find(bulkTickets, t => t.id === Number(value));
    if (nextTicket) {
      nextTicket.use();
      const newSelectedBulkTickets = [...selectedBulkTickets];
      newSelectedBulkTickets[index] = nextTicket;
      setSelectedBulkTickets(newSelectedBulkTickets);
    }
    if (price?.bulkTicketIds) {
      price.bulkTicketIds[index] = value.toString();
    }
  };

  const handleBulkTicketFragmentChange = (index: number, value: number): void => {
    const price = orderItem.price;
    if (price?.bulkTicketFragmentIds) {
      price.bulkTicketFragmentIds[index] = value;
    }
  };

  const handleClickTravelerAdd = () => {
    try {
      // 永続化していない編集データの管理のために仮のidが必要
      // uuidのほうが安全だが、4桁の乱数程度でよいと判断
      const newId = Math.floor(Math.random() * (9999 + 1 - 1000)) + TRAVELER_INFORMATION_TEMP_ID_RANGE;
      const travelerInformation = new TravelerInformation({ id: newId });
      orderItem.travelerInformations.push(travelerInformation);
      orderItem.handlePeopleNumChange(orderItem.travelerInformations.length);
      changed?.();
      setShownTravelerPopupId(newId);
    } catch (errorList) {
      if (Array.isArray(errorList)) setErrors(errorList);
    }
  };

  const handleClickTravelerEdit = (id: number) => {
    const newId = shownTravelerPopupId === id ? null : id;
    setShownTravelerPopupId(newId);
    setErrors([]);
  };

  const handleEditTraveler = (travelerInformation: TravelerInformation) => {
    try {
      if (travelerInformation.id === undefined) return;
      handleClickTravelerEdit(travelerInformation.id);
      changed?.();
    } catch (errorList) {
      if (Array.isArray(errorList)) setErrors(errorList);
    }
  };

  const handleDeleteTraveler = (traveler: TravelerInformation) => {
    if (traveler.id === undefined) return;
    orderItem.removeTraveler(traveler.id);
    changed?.();
  };

  const syncDisplayPriceFromDetails = () => {
    const orderItemPrice = orderItem.price;
    if (!orderItemPrice) return;
    orderItemPrice.price = orderItemPrice.calcPriceFromDetails;
  };

  const handleAddPriceDetail = (): void => {
    const orderItemPrice = orderItem.price;
    const priceDetails = orderItemPrice?.priceDetails;
    if (priceDetails) {
      priceDetails.push(
        new OrderItemPriceDetail({
          name: '',
          price: 0,
          orderItemPriceId: orderItemPrice.id || 0,
          taxTypeId: orderItemPrice.taxTypeId || TAX_TYPE_ID.TAXABLE_10_ID
        })
      );
    }
    syncDisplayPriceFromDetails();
    changed?.();
  };

  const handleDeletePriceDetail = (index: number): void => {
    if (confirm('内訳を削除しますか？')) {
      const priceDetails = orderItem.price?.priceDetails;
      if (priceDetails) {
        priceDetails.splice(index, 1);
      }
      syncDisplayPriceFromDetails();
      changed?.();
    }
  };

  const getSuppliedItemId = (
    individualTargetSuppliedItems: IndividualTargetSuppliedItem[],
    elements: Element[],
    targetType: string
  ): number | undefined => {
    let airlineCompanyType = '';
    const elementName = elements[0].name || '';

    const jalRegex = /(jal|jac|jta|rac)/i;
    const anaRegex = /ana/i;
    const sfjRegex = /sfj/i;

    if (jalRegex.test(elementName)) {
      airlineCompanyType = 'JAL';
    } else if (anaRegex.test(elementName)) {
      airlineCompanyType = 'ANA';
    } else if (sfjRegex.test(elementName)) {
      airlineCompanyType = 'SFJ';
    }

    const matchedItem = individualTargetSuppliedItems.find(item => {
      return item.airline_company_type === airlineCompanyType && item.target_type === targetType;
    });

    if (matchedItem) {
      return matchedItem.supplied_item_id;
    }

    return undefined;
  };

  const getShareholderInfoOriginalPrice = (
    originalPrices: OrderItemOriginalPrice[],
    elements: Element[],
    shareholderInfos: ShareholderInfo[],
    targetType: string
  ): number => {
    const targetSupplierType = targetType.includes('アリーズ') ? 'allies' : 'frontier';
    const purchasedAt = moment(originalPrices[0].purchasedAt);
    const elementName = elements[0].name || '';

    let priceKey = '';

    const jalRegex = /(jal|jac|jta|rac)/i;
    const anaRegex = /ana/i;
    const sfjRegex = /sfj/i;

    if (jalRegex.test(elementName)) {
      priceKey = 'purchase_price_jal';
    } else if (anaRegex.test(elementName)) {
      priceKey = 'purchase_price_ana';
    } else if (sfjRegex.test(elementName)) {
      priceKey = 'purchase_price_sfj';
    }

    const selectedPriceInfo = shareholderInfos.find(info => {
      const from = moment(info.from);
      const to = info.to !== null ? moment(info.to) : moment('9999-12-31');

      const fromDate = from.format('YYYY-MM-DD');
      const toDate = to.format('YYYY-MM-DD');
      const purchasedDate = purchasedAt.format('YYYY-MM-DD');

      return (
        purchasedDate >= fromDate &&
        purchasedDate <= toDate &&
        info.supplier_type === targetSupplierType &&
        priceKey
      );
    });

    if (selectedPriceInfo !== null && selectedPriceInfo !== undefined) {
      const selectedPrice = selectedPriceInfo[priceKey];
      if (selectedPrice !== null && selectedPrice !== undefined) {
        return selectedPrice;
      }
    }

    return 0;
  };

  const handleAddOriginalPriceCommon = (
    originalPrices: OrderItemOriginalPrice[],
    elements: Element[],
    shareholderInfos: ShareholderInfo[],
    targetType: string
  ): void => {
    const price = getShareholderInfoOriginalPrice(originalPrices, elements, shareholderInfos, targetType);
    const suppliedItemId = getSuppliedItemId(individualTargetSuppliedItems, elements, targetType);

    if (suppliedItems && originalPrices) {
      originalPrices.push(
        new OrderItemOriginalPrice({
          orderItemOriginalPriceType: orderItem.orderItemType,
          price,
          paymentMethodId: suppliedItems.find(item => item.id === suppliedItemId)?.paymentMethodId || undefined,
          suppliedItemId,
          purchasedAt: moment(originalPrices[0].purchasedAt)
        })
      );
      changed?.();
    }
  };

  const handleAddAlliesOriginalPrice = (): void => {
    const originalPrices = orderItem.price?.originalPrices || [];
    const { elements } = orderItem;

    handleAddOriginalPriceCommon(originalPrices, elements, shareholderInfos, 'アリーズ株主優待券');
  };

  const handleAddFrontierOriginalPrice = (): void => {
    const originalPrices = orderItem.price?.originalPrices || [];
    const { elements } = orderItem;

    handleAddOriginalPriceCommon(originalPrices, elements, shareholderInfos, 'フロンティア株主優待券');
  };

  const handleAddOriginalPrice = (suppliedItemId: number): void => {
    const originalPrices = orderItem.price?.originalPrices || [];
    if (suppliedItems && originalPrices) {
      originalPrices.push(
        new OrderItemOriginalPrice({
          orderItemOriginalPriceType: orderItem.orderItemType,
          price: 0,
          paymentMethodId: suppliedItems.find(item => item.id === suppliedItemId)?.paymentMethodId || undefined
        })
      );
    }
    changed?.();
  };

  const handleDeleteOriginalPrice = (index: number): void => {
    if (confirm('新卸値を削除しますか？')) {
      const originalPrices = orderItem.price?.originalPrices || [];
      if (originalPrices) {
        originalPrices.splice(index, 1);
      }
      changed?.();
    }
  };

  const findBulkTicketById = (id: string): BulkTicket => {
    const t = _.find(bulkTickets, t => {
      return `${t.id}` === id;
    });
    return t || bulkTickets[0];
  };

  const getTransportType = (orderItem: OrderItem): TransportType | null => {
    if (orderItem.transports.length) {
      return orderItem.transports[0].transportType;
    }
    if (orderItem.elements.length && orderItem.elements[0].type === 'transport') {
      const tr = orderItem.elements[0] as TransportElement;
      return tr.transportType;
    }
    return null;
  };

  const getTransportDate = (orderItem: OrderItem): Moment | null => {
    if (orderItem.transports.length) {
      return orderItem.transports[0].from.time;
    }
    if (orderItem.elements.length && orderItem.elements[0].type === 'transport') {
      const tr = orderItem.elements[0] as TransportElement;
      return tr.from.time;
    }
    return null;
  };

  const openShareholderTicketFees = () => {
    const organizationId = selectedTrip.user.organization?.id;
    const params = new URLSearchParams();

    if (organizationId) params.set('organization_id', String(organizationId));

    const query = params.toString();
    const path = query ? `/arrangement/shareholder_ticket_fees?${query}` : '/arrangement/shareholder_ticket_fees';

    window.open(path, '_blank', 'noopener,noreferrer');
  };

  const isMatchTargetSuppliedItem = (
    individualTargetSuppliedItems: IndividualTargetSuppliedItem[],
    originalPrices: OrderItemOriginalPrice[],
    targetType: string
  ): boolean => {
    return individualTargetSuppliedItems.some(item =>
      originalPrices.some(
        originalPrice =>
          item.display_condition_supplied_item_id &&
          originalPrice.suppliedItemId &&
          item.display_condition_supplied_item_id === originalPrice.suppliedItemId &&
          item.target_type === targetType
      )
    );
  };

  try {
    const peopleNumArray: number[] = [];
    for (let i = 0; i < (orderItem.peopleNum || 0); i += 1) {
      peopleNumArray.push(i);
    }
    return (
      <Wrapper>
        <InputWrapperBase>
          <Label>利用者</Label>
          {orderItem.travelerInformations.map((t, i) => {
            const travelerId = t.id;
            return (
              <TravelerBlock key={i}>
                <span>
                  {t.lastNameKana && t.lastNameKana.trim().length
                    ? // mobxの影響で入力中にundefinedが表示される対策
                      [t.lastNameKana, t.firstNameKana].filter(Boolean).join(' ').trim()
                    : t.user?.email ?? '（入力中）'}
                  {t.flightBirthday ? `(${t.flightBirthday.format('YYYY/MM/DD')})` : '(未設定)'}
                </span>
                <Link
                  type="button"
                  onClick={() => travelerId !== undefined && handleClickTravelerEdit(travelerId)}
                  data-testid="traveler-edit-button"
                >
                  編集
                </Link>

                <DeleteButton onClick={() => handleDeleteTraveler(t)}>×</DeleteButton>

                {shownTravelerPopupId === travelerId && travelerId !== undefined && (
                  <StyledOrderItemTravelerForm
                    travelerInformation={t}
                    candidates={candidates}
                    transportType={getTransportType(orderItem)}
                    transportDate={getTransportDate(orderItem)}
                    candidatesLoading={candidatesLoading}
                    errors={errors}
                    onSubmit={() => handleEditTraveler(t)}
                    onCancel={() => handleClickTravelerEdit(travelerId)}
                    onSearchCandidates={fetchCandidates}
                    classNameForModifiedField={(...path) =>
                      classNameForModifiedField('travelerInformations', i, ...path)
                    }
                  />
                )}
              </TravelerBlock>
            );
          })}
          <IndentedWrapper>
            <AddButton onClick={handleClickTravelerAdd}>利用者を追加</AddButton>
          </IndentedWrapper>
        </InputWrapperBase>
        {orderItem.isAvailableBulkTicket ? null : (
          <>
            <InputWrapper>
              <Label>代金(ユーザーに見せる)</Label>
              <p>
                {orderItem.price.price.toLocaleString()}円 (上限：
                {orderItem.price && utils.digits(orderItem.price?.budgetFrameAmount)}円)
              </p>
              {getTransportType(orderItem) === 'domestic_air' && (
                <ActionButton type="button" onClick={openShareholderTicketFees}>
                  株優確認
                </ActionButton>
              )}
            </InputWrapper>
            <InputWrapperBase>
              {orderItem.price?.priceDetails &&
                openPriceDetails &&
                orderItem.price?.priceDetails.map((priceDetail, index) => (
                  <div key={index}>
                    <IndentedWrapper>
                      <Label className={classNameForModifiedField('price', 'priceDetails', index, 'name')}>
                        {index + 1}.項目名
                      </Label>
                      <input
                        type="text"
                        value={priceDetail.name}
                        onChange={e => {
                          priceDetail.name = e.target.value;
                        }}
                        onBlur={() => changed?.()}
                      />
                      {index !== 0 && !priceDetail.id && (
                        <DeleteButton onClick={e => handleDeletePriceDetail(index)}>×</DeleteButton>
                      )}
                    </IndentedWrapper>
                    <Depth2IndentedWrapper>
                      <Label className={classNameForModifiedField('price', 'priceDetails', index, 'price')}>
                        金額
                      </Label>
                      <PriceField
                        initialValue={priceDetail.price}
                        onChange={value => {
                          priceDetail.price = value;
                          syncDisplayPriceFromDetails();
                          changed?.();
                        }}
                      />
                      <p>円</p>
                    </Depth2IndentedWrapper>
                    <Depth2IndentedWrapper>
                      <Label className={classNameForModifiedField('price', 'priceDetails', index, 'taxTypeId')}>
                        税区分
                      </Label>
                      <select
                        value={priceDetail.taxTypeId}
                        onChange={e => {
                          priceDetail.taxTypeId = parseInt(e.target.value, 10);
                          changed?.();
                        }}
                      >
                        {taxTypes.map(taxType => (
                          <option key={taxType.id} value={taxType.id}>
                            {taxType.name}
                          </option>
                        ))}
                      </select>
                    </Depth2IndentedWrapper>
                  </div>
                ))}
              {openPriceDetails ? (
                <IndentedWrapper>
                  <AddButton onClick={e => handleAddPriceDetail()}>内訳を追加</AddButton>
                  {orderItem.orderItemCategory === 'foreign_air' && (
                    <ActionButton color="sub" onClick={() => setOpenPriceDetails(false)}>
                      内訳を閉じる
                    </ActionButton>
                  )}
                </IndentedWrapper>
              ) : (
                <IndentedWrapper>
                  <ActionButton onClick={() => setOpenPriceDetails(true)}>内訳を開く</ActionButton>
                </IndentedWrapper>
              )}
            </InputWrapperBase>
            {orderItem.price?.originalPrices && (
              <InputWrapperBase>
                <InputWrapper>
                  <Label>新卸値(ユーザーに見せない)</Label>
                  <p>{orderItem.price.originalTotalPrice.toLocaleString()}円</p>
                </InputWrapper>
                <div>
                  {orderItem.price.originalPrices.map((originalPrice, index) => (
                    <div key={index}>
                      <IndentedWrapper>
                        <Label className={classNameForModifiedField('price', 'originalPrices', index, 'price')}>
                          {index + 1}.
                        </Label>
                        <PriceField
                          initialValue={originalPrice.price}
                          onChange={value => {
                            originalPrice.price = value;
                            changed?.();
                          }}
                        />
                        <p>円</p>
                        {index !== 0 && !originalPrice.id && (
                          <DeleteButton onClick={e => handleDeleteOriginalPrice(index)}>×</DeleteButton>
                        )}
                      </IndentedWrapper>
                      <Depth2IndentedWrapper>
                        <Label
                          className={classNameForModifiedField('price', 'originalPrices', index, 'taxTypeId')}
                        >
                          税区分(仕入管理用)
                        </Label>
                        <select
                          value={originalPrice.taxTypeId || '2'}
                          onChange={e => {
                            originalPrice.taxTypeId = _.isEmpty(e.target.value)
                              ? undefined
                              : parseInt(e.target.value, 10);
                            changed?.();
                          }}
                        >
                          {taxTypes.map(taxType => (
                            <option key={taxType.id} value={taxType.id}>
                              {taxType.name}
                            </option>
                          ))}
                        </select>
                      </Depth2IndentedWrapper>
                      {suppliedItems && (
                        <Depth2IndentedWrapper>
                          <Label
                            className={classNameForModifiedField(
                              'price',
                              'originalPrices',
                              index,
                              'suppliedItemId'
                            )}
                          >
                            仕入商品
                          </Label>
                          <OrderItemSupplierItemSelect
                            suppliedItems={_.filter(suppliedItems, item => item.disabled === false)}
                            onSelect={(v: number) => {
                              originalPrice.suppliedItemId = v;
                              originalPrice.paymentMethodId =
                                suppliedItems.find(s => s.id === v)?.paymentMethodId || undefined;
                              changed?.();
                            }}
                            suppliedItemId={originalPrice.suppliedItemId}
                          />
                        </Depth2IndentedWrapper>
                      )}
                      <Depth2IndentedWrapper>
                        <Label
                          className={classNameForModifiedField(
                            'price',
                            'originalPrices',
                            index,
                            'paymentMethodId'
                          )}
                        >
                          仕入先決済方法
                        </Label>
                        <div>
                          <Select
                            value={originalPrice.paymentMethodId?.toString() || ''}
                            onChange={e => {
                              originalPrice.paymentMethodId = parseInt(e.target.value, 10) || undefined;
                              changed?.();
                            }}
                            disabled={(orderItem.price?.originalPrices?.length || 0) < 1}
                          >
                            <option value="">
                              {paymentMethods.default(originalPrice.defaultPaymentMethod(suppliedItems) || 0)
                                ? 'デフォルト値を設定'
                                : '未設定'}
                            </option>
                            {paymentMethods.list.map(paymentMethod => (
                              <option key={paymentMethod.id} value={paymentMethod.id?.toString() || ''}>
                                {paymentMethod.codeWithName()}
                              </option>
                            ))}
                          </Select>
                          <Default>
                            デフォルト：
                            {paymentMethods.default(originalPrice.defaultPaymentMethod(suppliedItems) || 0)}
                          </Default>
                        </div>
                      </Depth2IndentedWrapper>
                      <Depth2IndentedWrapper>
                        <Label
                          className={classNameForModifiedField('price', 'originalPrices', index, 'purchasedAt')}
                        >
                          仕入日
                        </Label>
                        <Datepicker
                          value={
                            originalPrice.purchasedAt?.isValid() ? originalPrice.purchasedAt.toDate() : undefined
                          }
                          onChange={d => {
                            originalPrice.purchasedAt = d ? moment(d) : undefined;
                            changed?.();
                          }}
                          dateFormat="YYYY年MM月DD日"
                          placeholder="年/月/日"
                        />
                      </Depth2IndentedWrapper>
                    </div>
                  ))}
                </div>
                <IndentedWrapperWithGap>
                  <AddButton onClick={() => handleAddOriginalPrice(0)}>新卸値を追加</AddButton>
                  {serviceId === 1 && orderItem.price.originalPrices.length > 0 && (
                    <>
                      {isMatchTargetSuppliedItem(
                        individualTargetSuppliedItems,
                        orderItem.price.originalPrices,
                        'アリーズ株主優待券'
                      ) && (
                        <AddButtonWithMarginLeft onClick={() => handleAddAlliesOriginalPrice()}>
                          アリーズ株優を追加
                        </AddButtonWithMarginLeft>
                      )}
                      {isMatchTargetSuppliedItem(
                        individualTargetSuppliedItems,
                        orderItem.price.originalPrices,
                        'フロンティア株主優待券'
                      ) && (
                        <AddButtonWithMarginLeft onClick={() => handleAddFrontierOriginalPrice()}>
                          フロンティア株優を追加
                        </AddButtonWithMarginLeft>
                      )}
                    </>
                  )}
                </IndentedWrapperWithGap>
              </InputWrapperBase>
            )}
            {orderItem.transports[0]?.ticketingType === 1 ? (
              <InputWrapper>
                <Label className={classNameForModifiedField('transports', 0, 'alliesApplicationNumber')}>
                  アリーズ申込識別番号
                </Label>
                <input
                  type="text"
                  value={orderItem.transports[0].alliesApplicationNumber || ''}
                  onChange={e => {
                    orderItem.transports[0].alliesApplicationNumber = e.target.value;
                    changed?.();
                  }}
                  onBlur={() => changed?.()}
                />
              </InputWrapper>
            ) : orderItem.transports[0]?.ticketingType === 5 ? (
              <InputWrapper>
                <Label className={classNameForModifiedField('transports', 0, 'frontierApplicationNumber')}>
                  フロンティア申込識別番号
                </Label>
                <input
                  type="text"
                  value={orderItem.transports[0]?.frontierApplicationNumber || ''}
                  onChange={e => {
                    orderItem.transports[0].frontierApplicationNumber = e.target.value;
                    changed?.();
                  }}
                  onBlur={() => changed?.()}
                />
              </InputWrapper>
            ) : orderItem.transports[0]?.ticketingType === 6 ? (
              <InputWrapper>
                <Label className={classNameForModifiedField('transports', 0, 'skytoursApplicationNumber')}>
                  スカイツアーズ申込識別番号
                </Label>
                <input
                  type="text"
                  value={orderItem.transports[0]?.skytoursApplicationNumber || ''}
                  onChange={e => {
                    orderItem.transports[0].skytoursApplicationNumber = e.target.value;
                    changed?.();
                  }}
                  onBlur={() => changed?.()}
                />
              </InputWrapper>
            ) : null}
            {utils.isAiTravel(serviceId) && getTransportType(orderItem) === 'foreign_air' && (
              <InputWrapper>
                <Label className={classNameForModifiedField('tsdInvoiceNumber')}>ティ・エス・ディ請求書番号</Label>
                <input
                  type="text"
                  value={orderItem.tsdInvoiceNumber || ''}
                  onChange={e => {
                    orderItem.tsdInvoiceNumber = e.target.value;
                    changed?.();
                  }}
                />
              </InputWrapper>
            )}
            {getTransportType(orderItem) === 'foreign_air' && (
              <InputWrapper>
                <Label className={classNameForModifiedField('supplierReservationNumber')}>発券元予約番号</Label>
                <input
                  type="text"
                  value={orderItem.supplierReservationNumber || ''}
                  onChange={e => {
                    orderItem.supplierReservationNumber = e.target.value;
                    changed?.();
                  }}
                />
              </InputWrapper>
            )}
            <InputWrapper>
              <Label className={classNameForModifiedField('price', 'margin', 'amount')}>手数料</Label>
              <PriceField
                initialValue={orderItem.price?.margin?.amount}
                onChange={value => {
                  if (orderItem.price?.margin) {
                    orderItem.price.margin.amount = value;
                    changed?.();
                  }
                }}
              />
              <p>円</p>
              <ReCalcButton
                onClick={e => {
                  e.preventDefault();
                  if (orderItem.price?.margin) {
                    orderItem.price.calcMarginAmount();
                  }
                  changed?.();
                }}
              >
                再計算
              </ReCalcButton>
            </InputWrapper>
          </>
        )}
        <InputWrapper>
          <Label className={classNameForModifiedField('price', 'refund')}>返金予定額（仕訳用）</Label>
          <PriceField
            initialValue={orderItem.price?.refund}
            onChange={value => {
              if (orderItem.price) {
                orderItem.price.refund = value;
              }
              changed?.();
            }}
          />
          <p>円</p>
        </InputWrapper>
        <InputWrapper>
          <Label className={classNameForModifiedField('payment', 'paymentType')}>支払い方法</Label>
          <select
            value={orderItem.payment?.paymentType}
            onChange={e => {
              if (orderItem.payment) {
                orderItem.payment.paymentType = parseInt(e.target.value, 10);
              }
              changed?.();
            }}
          >
            {theme.themeClass === 'aitravel' && <option value={0}>クレジットカード</option>}
            <option value={1}>請求書</option>
            <option value={2}>現地払い</option>
            <option value={3}>回数券</option>
          </select>
        </InputWrapper>
        <InputWrapper>
          <Label
            className={classNameForModifiedField('price', 'selfLoss')}
            htmlFor={`trip_${selectedTrip.id}-item_${index}-selfLoss`}
          >
            自損による損失
          </Label>
          <input
            type="checkbox"
            id={`trip_${selectedTrip.id}-item_${index}-selfLoss`}
            checked={orderItem.price?.selfLoss || false}
            onChange={e => {
              if (orderItem.price) {
                orderItem.price.selfLoss = e.target.checked;
              }
              changed?.();
            }}
          />
        </InputWrapper>
        <InputWrapper>
          <Label
            className={classNameForModifiedField('price', 'debtWaiver')}
            htmlFor={`trip_${selectedTrip.id}-item_${index}-debtWaiver`}
          >
            請求権放棄による損金
          </Label>
          <input
            type="checkbox"
            id={`trip_${selectedTrip.id}-item_${index}-debtWaiver`}
            checked={orderItem.price?.debtWaiver || false}
            onChange={e => {
              if (orderItem.price) {
                orderItem.price.debtWaiver = e.target.checked;
              }
              changed?.();
            }}
          />
        </InputWrapper>
        <InputWrapper>
          <Label className={classNameForModifiedField('price', 'lossWorkflowUrl')}>ワークフローURL</Label>
          <input
            type="text"
            value={orderItem.price?.lossWorkflowUrl || ''}
            onChange={e => {
              if (orderItem.price) {
                orderItem.price.lossWorkflowUrl = e.target.value;
              }
              changed?.();
            }}
            onBlur={() => changed?.()}
          />
        </InputWrapper>
        {orderItem.isChangeableExist && (
          <>
            {orderItem.price?.shareholderTicketIds.map((shareholderTicketId, i) => (
              <div key={i}>
                <InputWrapper>
                  <Label className={classNameForModifiedField('price', 'shareholderTicketIds', i)}>
                    株優 {i + 1}
                  </Label>
                  <AutocompleteSelect
                    style={{ minWidth: '200px', width: '65%' }}
                    options={(() => {
                      const noneOption = { value: '', displayName: 'なし' };
                      const ticketOptions = shareholderTickets.list
                        .filter(t => !t.tripId || t.tripId === selectedTrip.id)
                        .map(t => ({
                          value: t.id?.toString() || '',
                          displayName: t.summary()
                        }));
                      return [noneOption, ...ticketOptions];
                    })()}
                    value={(() => {
                      const currentValue = shareholderTicketId?.toString() || '';

                      const options = [
                        { value: '', displayName: '' },
                        ...shareholderTickets.list
                          .filter(t => !t.tripId || t.tripId === selectedTrip.id)
                          .map(t => ({
                            value: t.id?.toString() || '',
                            displayName: t.summary()
                          }))
                      ];
                      return options.find(option => option.value === currentValue) || null;
                    })()}
                    getOptionLabel={option => option?.displayName || ''}
                    onChange={(_event, newValue) => {
                      if (orderItem.price) {
                        orderItem.price.shareholderTicketIds[i] = parseInt(newValue?.value || '', 10);
                      }
                      changed?.();
                    }}
                    placeholder="株優を選択"
                  />
                  <DeleteButton
                    onClick={() => {
                      if (orderItem.price) {
                        orderItem.price.shareholderTicketIds.splice(i, 1);
                      }
                      changed?.();
                    }}
                  >
                    ×
                  </DeleteButton>
                </InputWrapper>
                {(() => {
                  const currentTicket = shareholderTickets.find(shareholderTicketId);
                  if (currentTicket) {
                    return (
                      <KabuyuDescription>
                        選択中の株優
                        <br />
                        <Text text={currentTicket.description()} />
                      </KabuyuDescription>
                    );
                  }
                  return null;
                })()}
              </div>
            ))}
            <IndentedWrapper>
              <AddButton
                onClick={() => {
                  if (orderItem.price) {
                    orderItem.price.shareholderTicketIds.push(0);
                  }
                  changed?.();
                }}
              >
                株優を追加
              </AddButton>
            </IndentedWrapper>
          </>
        )}
        {orderItem.isAvailableBulkTicket ? (
          <>
            {peopleNumArray.map(i => (
              <div key={i}>
                <InputWrapper>
                  <Label className={classNameForModifiedField('price', 'bulkTicketIds', i)}>回数券(冊)</Label>
                  <select
                    value={orderItem.price?.bulkTicketIds[i]}
                    onChange={e => {
                      handleBulkTicketChange(i, parseInt(e.target.value, 10));
                      changed?.();
                    }}
                  >
                    <option value="">なし</option>
                    {bulkTickets.map(t =>
                      t.unused > 0 ||
                      t.id === parseInt(orderItem.price?.bulkTicketIds[i]?.toString() || '', 10) ? (
                        <option key={t.id} value={t.id}>
                          {t.summary}
                        </option>
                      ) : null
                    )}
                  </select>
                </InputWrapper>
                <InputWrapper>
                  {(orderItem.price?.bulkTicketIds[i] || selectedBulkTickets[i]) && (
                    <>
                      <FragmentLabel className={classNameForModifiedField('price', 'bulkTicketFragmentIds', i)}>
                        券番
                      </FragmentLabel>
                      <select
                        value={orderItem.price?.bulkTicketFragmentIds[i]}
                        onChange={e => {
                          handleBulkTicketFragmentChange(i, parseInt(e.target.value, 10));
                          changed?.();
                        }}
                      >
                        <option value="">なし</option>
                        {selectedBulkTickets[i] ? (
                          <>
                            {/* 新規で設定するケース */}
                            {selectedBulkTickets[i].bulkTicketFragments.map(f =>
                              f.status === 0 ? (
                                <option key={f.id} value={f.id}>
                                  {f.number}
                                </option>
                              ) : null
                            )}
                          </>
                        ) : (
                          <>
                            {/* すでに紐付られているケース */}
                            {findBulkTicketById(
                              orderItem.price?.bulkTicketIds[i]?.toString() || ''
                            ).bulkTicketFragments.map(f =>
                              f.status === 0 ||
                              f.id ===
                                parseInt(orderItem.price?.bulkTicketFragmentIds[i]?.toString() || '', 10) ? (
                                <option key={f.id} value={f.id}>
                                  {f.number}
                                </option>
                              ) : null
                            )}
                          </>
                        )}
                      </select>
                    </>
                  )}
                </InputWrapper>
              </div>
            ))}
          </>
        ) : null}
      </Wrapper>
    );
  } catch (e) {
    reportError(e);
    return null;
  }
};

const Wrapper = styled.div`
  background-color: ${props => props.theme.grayColorLight};
  padding: 5px;
`;

const InputWrapperBase = styled.div`
  margin-top: 5px;
`;

const InputWrapper = styled(InputWrapperBase)`
  display: flex;
  align-items: center;
`;

const Select = styled.select`
  max-width: 200px;
`;

const ReCalcButton = styled(Button)`
  padding: 5px;
  margin-left: 5px;
  min-width: 60px;
`;

const AddButton = styled(Button)`
  padding: 5px;
  min-width: 60px;
`;

const AddButtonWithMarginLeft = styled(Button)`
  padding: 5px;
  margin-left: 5px;
`;

const ActionButton = styled(NewButton)`
  padding: 5px;
  margin-left: 5px;
  min-width: 60px;
`;

const DeleteButton = styled(Button)`
  padding: 0px;
  margin-left: 20px;
  min-width: 20px;
  height: 20px;
  border-radius: 10px;
`;

const Label = styled.label`
  flex-shrink: 0;
  margin-right: 5px;
`;

const IndentedWrapper = styled.div`
  position: relative;
  display: flex;
  gap: 10px;
  padding-left: 40px;
`;

const Depth2IndentedWrapper = styled.div`
  display: flex;
  padding-left: 64px;

  &:last-child {
    padding-bottom: 10px;
  }
`;

const IndentedWrapperWithGap = styled.div`
  position: relative;
  display: flex;
  padding-left: 40px;
  gap: 5px;
`;

const KabuyuDescription = styled.div`
  font-size: 10px;
  border: 1px solid ${props => props.theme.grayBorderColor};
  background: ${props => props.theme.grayColorLight};
  padding: 2px;
  margin-left: 10px;
`;

const TravelerBlock = styled(IndentedWrapper)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 5px;

  > a {
    margin-left: 4px;
  }

  ${DeleteButton} {
    margin-left: 4px;
  }
`;

const StyledOrderItemTravelerForm = styled(OrderItemTravelerForm)`
  position: absolute;
  bottom: -5px;
  width: 300px;
  background-color: #fff;
  border: 1px solid #333;
  padding: 5px;
  transform: translateY(100%);
  z-index: 1;
`;

const FragmentLabel = styled.label`
  margin-left: 30px;
`;

const Default = styled.div`
  font-size: 0.9em;
  margin-top: 4px;
  color: ${props => props.theme.grayTextColor};
`;

export default withTheme(observer(OrderItemPriceForm));
